import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { SearchParametersDwr } from "../ls/LSSearch";
import axios from "axios";
import { WorldBookResultRecord } from "./WorldBookResultRecord";
import { WorldBookResultsPageLoader } from "./WorldBookSearchResults";
import { LoaderData } from "../../account/MyAccount";

interface WorldBookGetRecordsResponse {
  success: boolean;
  totalHits: number;
  results: WorldBookResultRecord[];
}

interface WorldBookSearchContextType {
  /**
   * @param page 1 based page index
   */
  setPage: (page: number) => void;
  setDb: (db: string) => void;
  searchParams: { page: number; term: string; db: string };
  totalHits: Readonly<number | null>;
  results: Readonly<WorldBookResultRecord[] | null>;
  resultsKey: Readonly<string | null>;
  loadingResults: Readonly<boolean>;
  pageSize: number;
}

const WorldBookSearchContext = createContext<
  WorldBookSearchContextType | undefined
>(undefined);

export const useWorldBookSearch = () => {
  const ctx = useContext(WorldBookSearchContext);
  if (!ctx)
    throw new Error(
      "useWorldBookSearch must be used within WorldBookSearchProvider"
    );
  return ctx;
};

function getWorldBookResultRecordsKey(results: WorldBookResultRecord[]) {
  if (!results) {
    return null;
  }
  return results
    .map((r) => r.docid)
    .sort()
    .toString();
}

export const WorldBookSearchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { payload } = useLoaderData() as LoaderData<
    typeof WorldBookResultsPageLoader
  >;

  const { default: defaultDb, selected: selectedDb } = payload.dbOptions;

  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [results, setResults] = useState<WorldBookResultRecord[] | null>(null);

  const resultsKey = useMemo(
    () => getWorldBookResultRecordsKey(results),
    [results]
  );

  const state = useMemo(() => {
    const term = searchParams.get("term");
    const db = searchParams.get("db") || selectedDb || defaultDb?.value;
    const pageParam = parseInt(searchParams.get("page") || "1", 10);
    const page = pageParam < 1 ? 1 : pageParam;
    return {
      term,
      page,
      db,
    };
  }, [searchParams, defaultDb, selectedDb]);

  useEffect(() => {
    if (!state) {
      return;
    }
    setLoadingResults(true);
    const { term, page, db } = state;
    search(term, page, db).then((response) => {
      setTotalHits(response.totalHits);
      setResults(response.results);
      setLoadingResults(false);
    });
  }, [state.page, state.term, state.db]);

  // DO NOT CHANGE THIS VALUE! This must be 15 because the World Book API only supports 15 results per page.
  const pageSize = 15;

  const search = async (term: string, page: number = 1, db: string) => {
    const startIndex = page <= 1 ? 1 : (page - 1) * pageSize + 1;

    const database = db || selectedDb || defaultDb?.value;

    const payload: SearchParametersDwr = {
      searchTerm: term,
      hitsPerPage: pageSize,
      sortCriteria: "Relevancy",
      startIndex,
      worldBookDatabaseList: [database],
    };

    try {
      const { data } = await axios.post<WorldBookGetRecordsResponse>(
        `/worldbook/search`,
        payload
      );
      return data;
    } catch (e: unknown) {
      return {
        success: false,
        totalHits: 0,
        results: [],
      };
    }
  };

  const setPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const setDb = (db: string) => {
    const next = new URLSearchParams(searchParams);
    if (db && db !== defaultDb?.value) next.set("db", db);
    else next.delete("db");
    next.set("page", "1");
    setSearchParams(next);
  };

  const contextValue: WorldBookSearchContextType = {
    setPage,
    setDb,
    searchParams: {
      page: state.page,
      term: state.term,
      db: state.db,
    },
    totalHits,
    results,
    resultsKey,
    loadingResults,
    pageSize,
  };

  return (
    <WorldBookSearchContext.Provider value={contextValue}>
      {children}
    </WorldBookSearchContext.Provider>
  );
};

export default WorldBookSearchProvider;
